import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Calendar, 
  Ticket, 
  ShoppingCart, 
  Users, 
  DollarSign,
  TrendingUp,
  Database,
  CreditCard,
  CheckCircle,
  XCircle,
  AlertCircle,
  Cloud,
  RefreshCw
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

import { API } from "../../config/api";

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    totalOrders: 0,
    paidOrders: 0,
    totalRevenue: 0,
    totalEvents: 0,
    totalGalleryImages: 0,
    totalInquiries: 0
  });
  const [stripeStatus, setStripeStatus] = useState(null);
  const [integrationsStatus, setIntegrationsStatus] = useState(null);
  const [dbInfo, setDbInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [seedingPages, setSeedingPages] = useState(false);
  const [seedResults, setSeedResults] = useState(null);
  const [cloudinaryMigrating, setCloudinaryMigrating] = useState(false);
  const [cloudinaryHealth, setCloudinaryHealth] = useState(null);
  const [cloudinaryMigrateResults, setCloudinaryMigrateResults] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchStripeStatus();
    fetchDbInfo();
    fetchIntegrationsStatus();
  }, []);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("admin_token")}`
  });

  const fetchDbInfo = async () => {
    try {
      const response = await fetch(`${API}/admin/db-info`, { headers: getAuthHeaders() });
      if (response.ok) {
        setDbInfo(await response.json());
      }
    } catch (error) {
      console.error("Error fetching DB info:", error);
    }
  };

  const fetchStats = async () => {
    try {
      const [ordersRes, eventsRes, galleryRes, inquiriesRes] = await Promise.all([
        fetch(`${API}/admin/orders`, { headers: getAuthHeaders() }),
        fetch(`${API}/admin/events`, { headers: getAuthHeaders() }),
        fetch(`${API}/admin/gallery`, { headers: getAuthHeaders() }),
        fetch(`${API}/admin/sponsor-inquiries`, { headers: getAuthHeaders() })
      ]);

      const orders = ordersRes.ok ? await ordersRes.json() : [];
      const events = eventsRes.ok ? await eventsRes.json() : [];
      const gallery = galleryRes.ok ? await galleryRes.json() : [];
      const inquiries = inquiriesRes.ok ? await inquiriesRes.json() : [];

      const paidOrders = orders.filter(o => o.status === "paid");
      const totalRevenue = paidOrders.reduce((sum, o) => sum + (o.amount_cents || 0), 0);

      setStats({
        totalOrders: orders.length,
        paidOrders: paidOrders.length,
        totalRevenue,
        totalEvents: events.length,
        totalGalleryImages: gallery.length,
        totalInquiries: inquiries.filter(i => i.status === "new").length
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeStatus = async () => {
    try {
      const response = await fetch(`${API}/admin/stripe-status`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setStripeStatus(await response.json());
      }
    } catch (error) {
      console.error("Error fetching Stripe status:", error);
    }
  };

  const fetchIntegrationsStatus = async () => {
    try {
      const response = await fetch(`${API}/admin/integrations-status`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setIntegrationsStatus(await response.json());
      }
    } catch (error) {
      console.error("Error fetching integrations status:", error);
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const response = await fetch(`${API}/admin/seed-data`, {
        method: "POST",
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success("Default data seeded successfully!");
        fetchStats();
      } else {
        toast.error("Failed to seed data");
      }
    } catch (error) {
      toast.error("Error seeding data");
    } finally {
      setSeeding(false);
    }
  };

  const handleSeedSitePages = async () => {
    setSeedingPages(true);
    setSeedResults(null);
    try {
      const response = await fetch(`${API}/admin/seed-site-pages`, {
        method: "POST",
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setSeedResults(data.results);
        const inserted = data.results.filter(r => r.status === "inserted").length;
        if (inserted > 0) {
          toast.success(`Initialized ${inserted} site page(s)!`);
        } else {
          toast.info("All site pages already exist.");
        }
        fetchDbInfo();
      } else {
        toast.error("Failed to seed site pages");
      }
    } catch (error) {
      toast.error("Error seeding site pages");
    } finally {
      setSeedingPages(false);
    }
  };

  const handleSetHomeHero = async () => {
    try {
      const response = await fetch(`${API}/admin/set-home-hero`, {
        method: "POST",
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success("Home hero image updated!");
      } else {
        toast.error("Failed to update hero image");
      }
    } catch (error) {
      toast.error("Error updating hero image");
    }
  };

  const handleCreatePicnic = async () => {
    try {
      const response = await fetch(`${API}/admin/create-picnic-event`, {
        method: "POST",
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Picnic event ${data.action}!`);
        fetchStats();
        fetchDbInfo();
      } else {
        toast.error("Failed to create picnic event");
      }
    } catch (error) {
      toast.error("Error creating picnic event");
    }
  };

  const handleFixPicnicAddress = async () => {
    try {
      const response = await fetch(`${API}/admin/fix-picnic-address`, {
        method: "POST",
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Picnic address fixed! Updated ${data.modified_by_title} events.`);
        if (data.updated_events && data.updated_events.length > 0) {
          console.log("Updated events:", data.updated_events);
        }
        fetchStats();
        fetchDbInfo();
      } else {
        toast.error("Failed to fix picnic address");
      }
    } catch (error) {
      toast.error("Error fixing picnic address");
    }
  };

  const handleFixGalleryUrls = async () => {
    try {
      const response = await fetch(`${API}/admin/migrate/gallery-urls`, {
        method: "POST",
        headers: {
          ...getAuthHeaders(),
          "X-MIGRATE-CONFIRM": "true"
        }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Gallery URLs fixed! Updated ${data.updated} of ${data.total} images.`);
        fetchStats();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to fix gallery URLs");
      }
    } catch (error) {
      toast.error("Error fixing gallery URLs");
    }
  };

  const handleSeedGallery = async () => {
    try {
      const response = await fetch(`${API}/admin/seed-gallery`, {
        method: "POST",
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchStats();
      } else {
        toast.error("Failed to seed gallery images");
      }
    } catch (error) {
      toast.error("Error seeding gallery images");
    }
  };

  const handleFixHeroImage = async () => {
    try {
      const response = await fetch(`${API}/admin/fix-hero-image`, {
        method: "POST",
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
      } else {
        toast.error("Failed to fix hero image");
      }
    } catch (error) {
      toast.error("Error fixing hero image");
    }
  };

  const handleCheckMediaHealth = async () => {
    try {
      const response = await fetch(`${API}/admin/media-orphans`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        if (data.orphans_found > 0) {
          toast.error(`Found ${data.orphans_found} broken media references!`);
          console.log("Orphaned media:", data.orphans);
        } else {
          toast.success("All media files accessible!");
        }
      } else {
        toast.error("Failed to check media health");
      }
    } catch (error) {
      toast.error("Error checking media health");
    }
  };

  // Cloudinary handlers
  const handleCloudinaryHealthCheck = async () => {
    try {
      const response = await fetch(`${API}/admin/cloudinary/media-health`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCloudinaryHealth(data);
        if (data.unstable > 0) {
          toast.warning(`${data.unstable} images need migration to Cloudinary`);
        } else {
          toast.success("All images are on Cloudinary!");
        }
      } else {
        toast.error("Failed to check Cloudinary health");
      }
    } catch (error) {
      toast.error("Error checking Cloudinary health");
    }
  };

  const handleCloudinaryMigrateAll = async () => {
    if (!window.confirm("This will migrate all non-Cloudinary images to Cloudinary. Continue?")) {
      return;
    }
    
    setCloudinaryMigrating(true);
    setCloudinaryMigrateResults(null);
    try {
      const response = await fetch(`${API}/admin/cloudinary/migrate-media`, {
        method: "POST",
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCloudinaryMigrateResults(data);
        if (data.migrated > 0) {
          toast.success(`Migrated ${data.migrated} images to Cloudinary!`);
        } else if (data.failed?.length > 0) {
          toast.error(`${data.failed.length} images failed to migrate`);
        } else {
          toast.info("No images needed migration");
        }
        // Refresh health check
        handleCloudinaryHealthCheck();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Migration failed");
      }
    } catch (error) {
      toast.error("Error during migration");
    } finally {
      setCloudinaryMigrating(false);
    }
  };

  const handleSetupMegaSponsor = async () => {
    try {
      const response = await fetch(`${API}/admin/setup-mega-sponsor`, {
        method: "POST",
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Mega Sponsor setup complete! Tier: ${data.results.tier.status}, Sponsor: ${data.results.sponsor.status}`);
        fetchDbInfo();
      } else {
        toast.error("Failed to setup Mega Sponsor");
      }
    } catch (error) {
      toast.error("Error setting up Mega Sponsor");
    }
  };

  const statCards = [
    {
      icon: ShoppingCart,
      label: "Total Orders",
      value: stats.totalOrders,
      color: "bg-blue-100 text-blue-600",
      link: "/admin/orders"
    },
    {
      icon: DollarSign,
      label: "Total Revenue",
      value: `$${(stats.totalRevenue / 100).toFixed(2)}`,
      color: "bg-green-100 text-green-600",
      link: "/admin/orders"
    },
    {
      icon: Calendar,
      label: "Active Events",
      value: stats.totalEvents,
      color: "bg-purple-100 text-purple-600",
      link: "/admin/events"
    },
    {
      icon: Users,
      label: "New Inquiries",
      value: stats.totalInquiries,
      color: "bg-orange-100 text-orange-600",
      link: "/admin/inquiries"
    }
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl text-kerala-green">Dashboard</h1>
          <p className="text-gray-600">Welcome to the Tatvamasi admin panel</p>
        </div>
        <Button
          onClick={handleSeedData}
          disabled={seeding}
          className="bg-kerala-green hover:bg-kerala-green/90"
          data-testid="seed-data-btn"
        >
          {seeding ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              Seeding...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Database className="w-4 h-4" />
              Seed Default Data
            </span>
          )}
        </Button>
      </div>

      {/* Stripe Status Panel */}
      <div className="bg-white p-6 rounded-sm border border-kerala-gold/10" data-testid="stripe-status-panel">
        <div className="flex items-center gap-3 mb-4">
          <CreditCard className="w-6 h-6 text-kerala-green" />
          <h2 className="font-heading text-xl text-kerala-green">Payment Status</h2>
        </div>
        
        {stripeStatus ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Connection Status */}
            <div className={`p-4 rounded-lg ${stripeStatus.connected ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                {stripeStatus.connected ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600" />
                )}
                <span className="font-medium">
                  {stripeStatus.connected ? 'Connected' : 'Not Connected'}
                </span>
              </div>
              <p className="text-sm text-gray-600">
                {stripeStatus.connected 
                  ? `Key: ${stripeStatus.key_prefix || 'Configured'}`
                  : 'Add STRIPE_SECRET_KEY to enable payments'
                }
              </p>
            </div>

            {/* Mode */}
            <div className={`p-4 rounded-lg ${
              stripeStatus.mode === 'live' ? 'bg-green-50' : 
              stripeStatus.mode === 'test' ? 'bg-yellow-50' : 'bg-gray-50'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                {stripeStatus.mode === 'live' ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : stripeStatus.mode === 'test' ? (
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-gray-400" />
                )}
                <span className="font-medium capitalize">
                  {stripeStatus.mode ? `${stripeStatus.mode} Mode` : 'Unknown Mode'}
                </span>
              </div>
              <p className="text-sm text-gray-600">
                {stripeStatus.mode === 'live' 
                  ? 'Processing real payments'
                  : stripeStatus.mode === 'test'
                  ? 'Using test card numbers'
                  : 'Configure Stripe key'
                }
              </p>
            </div>

            {/* Webhook Status */}
            <div className={`p-4 rounded-lg ${stripeStatus.webhook_configured ? 'bg-green-50' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                {stripeStatus.webhook_configured ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-gray-400" />
                )}
                <span className="font-medium">
                  {stripeStatus.webhook_configured ? 'Webhook Active' : 'Webhook Not Set'}
                </span>
              </div>
              <p className="text-sm text-gray-600">
                {stripeStatus.last_webhook_received 
                  ? `Last: ${new Date(stripeStatus.last_webhook_received).toLocaleDateString()}`
                  : 'No webhooks received yet'
                }
              </p>
            </div>
          </div>
        ) : (
          <div className="animate-pulse flex gap-4">
            <div className="h-20 bg-gray-100 rounded-lg flex-1"></div>
            <div className="h-20 bg-gray-100 rounded-lg flex-1"></div>
            <div className="h-20 bg-gray-100 rounded-lg flex-1"></div>
          </div>
        )}
      </div>

      {/* Environment Warning Banner */}
      {dbInfo && dbInfo.mongo_url_prefix?.includes("localhost") && (
        <div className="bg-yellow-100 border-2 border-yellow-400 rounded-lg p-4" data-testid="env-warning">
          <div className="flex items-center gap-3">
            <div className="bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-sm font-bold">
              ⚠️ PREVIEW ENVIRONMENT
            </div>
            <p className="text-yellow-800 text-sm">
              You are editing the <strong>preview database</strong>, not production. 
              Changes here will NOT appear on tatvamasi.ca until deployed.
            </p>
          </div>
        </div>
      )}

      {/* Production Indicator */}
      {dbInfo && !dbInfo.mongo_url_prefix?.includes("localhost") && (
        <div className="bg-green-100 border-2 border-green-400 rounded-lg p-4" data-testid="prod-indicator">
          <div className="flex items-center gap-3">
            <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold">
              ✓ PRODUCTION
            </div>
            <p className="text-green-800 text-sm">
              You are editing the <strong>production database</strong>. Changes will appear on tatvamasi.ca.
            </p>
          </div>
        </div>
      )}

      {/* Database Info Panel - Debug */}
      {dbInfo && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200" data-testid="db-info-panel">
          <div className="flex items-center gap-3 mb-3">
            <Database className="w-5 h-5 text-blue-600" />
            <h3 className="font-medium text-blue-800">System Debug Info</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-blue-500 text-xs">Build Version</p>
              <p className="font-mono text-blue-800">{dbInfo.build_version || "N/A"}</p>
            </div>
            <div>
              <p className="text-blue-500 text-xs">Database</p>
              <p className="font-mono text-blue-800">{dbInfo.db_name}</p>
            </div>
            <div>
              <p className="text-blue-500 text-xs">Mongo URL</p>
              <p className="font-mono text-blue-800 text-xs">{dbInfo.mongo_url_prefix || "N/A"}</p>
            </div>
            <div>
              <p className="text-blue-500 text-xs">Ticket Slots</p>
              <p className="font-mono text-blue-800">{dbInfo.collections?.ticket_slots || 0}</p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-blue-200">
            <p className="text-blue-500 text-xs mb-2">Collections</p>
            <div className="flex flex-wrap gap-3 text-xs text-blue-700">
              <span className="bg-blue-100 px-2 py-1 rounded">Site Pages: {dbInfo.collections?.site_pages || 0}</span>
              <span className="bg-blue-100 px-2 py-1 rounded">Events: {dbInfo.collections?.events || 0}</span>
              <span className="bg-blue-100 px-2 py-1 rounded">Gallery: {dbInfo.collections?.gallery_images || 0}</span>
              <span className="bg-blue-100 px-2 py-1 rounded">Members: {dbInfo.collections?.members || 0}</span>
              <span className="bg-blue-100 px-2 py-1 rounded">Orders: {dbInfo.collections?.orders || 0}</span>
            </div>
          </div>
          {dbInfo.ticket_slots_sample && dbInfo.ticket_slots_sample.length > 0 && (
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-blue-500 text-xs mb-2">Ticket Prices (DB Source of Truth)</p>
              <div className="flex flex-wrap gap-2 text-xs">
                {dbInfo.ticket_slots_sample.map(slot => (
                  <div key={slot.slot_id} className={`bg-white p-2 rounded border ${slot.is_visible ? 'border-green-300' : 'border-gray-300'}`}>
                    <p className="font-medium">{slot.slot_id} {slot.is_visible ? '(Visible)' : '(Hidden)'}</p>
                    {slot.ticket_types?.map(tt => (
                      <p key={tt.code} className="text-gray-600">
                        {tt.code}: ${tt.price} (cap: {tt.capacity_total || '∞'})
                      </p>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stats Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white p-6 rounded-sm border border-kerala-gold/10 animate-pulse">
              <div className="h-20"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, idx) => (
            <Link
              key={idx}
              to={stat.link}
              className="bg-white p-6 rounded-sm border border-kerala-gold/10 hover:shadow-md transition-shadow"
              data-testid={`stat-card-${idx}`}
            >
              <div className="flex items-start justify-between">
                <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6" />
                </div>
                <TrendingUp className="w-4 h-4 text-green-500" />
              </div>
              <p className="mt-4 text-2xl font-semibold text-kerala-green">{stat.value}</p>
              <p className="text-gray-600 text-sm">{stat.label}</p>
            </Link>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white p-6 rounded-sm border border-kerala-gold/10">
        <h2 className="font-heading text-xl text-kerala-green mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/admin/events">
            <Button variant="outline" className="w-full justify-start">
              <Calendar className="w-4 h-4 mr-2" />
              Manage Events
            </Button>
          </Link>
          <Link to="/admin/tickets">
            <Button variant="outline" className="w-full justify-start">
              <Ticket className="w-4 h-4 mr-2" />
              Manage Tickets
            </Button>
          </Link>
          <Link to="/admin/gallery">
            <Button variant="outline" className="w-full justify-start">
              <Calendar className="w-4 h-4 mr-2" />
              Add Gallery
            </Button>
          </Link>
          <Link to="/admin/content">
            <Button variant="outline" className="w-full justify-start">
              <Calendar className="w-4 h-4 mr-2" />
              Edit Content
            </Button>
          </Link>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-kerala-green/5 border border-kerala-green/20 rounded-sm p-6">
        <h3 className="font-heading text-lg text-kerala-green mb-2">Getting Started</h3>
        <ul className="text-gray-600 space-y-2 text-sm">
          <li>• <strong>All changes are live immediately</strong> - Updates in admin reflect on the public site instantly</li>
          <li>• Go to Events to create/edit events with dates, venues, and ticketing</li>
          <li>• Set up Ticket Types with pricing and capacity</li>
          <li>• Customize site content in the Content section (FAQs, Testimonials)</li>
          <li>• Add sponsor logos and tiers in the Sponsors section</li>
          <li>• Use Gallery to manage event photos</li>
        </ul>
      </div>

      {/* Quick Setup Panel */}
      <div className="bg-orange-50 border border-orange-200 rounded-sm p-6">
        <h3 className="font-heading text-lg text-orange-800 mb-4">Quick Setup (Production)</h3>
        <p className="text-orange-700 text-sm mb-4">Use these buttons to quickly set up production data:</p>
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={handleSeedSitePages}
            disabled={seedingPages}
            className="bg-orange-600 hover:bg-orange-700"
          >
            {seedingPages ? "Initializing..." : "1. Initialize Site Pages"}
          </Button>
          <Button
            onClick={handleSetHomeHero}
            className="bg-orange-600 hover:bg-orange-700"
          >
            2. Set Home Hero Image
          </Button>
          <Button
            onClick={handleCreatePicnic}
            className="bg-orange-600 hover:bg-orange-700"
          >
            3. Create Picnic Event
          </Button>
          <Button
            onClick={handleFixPicnicAddress}
            className="bg-red-600 hover:bg-red-700"
            data-testid="fix-picnic-address-btn"
          >
            4. Fix Picnic Address
          </Button>
          <Button
            onClick={handleSetupMegaSponsor}
            className="bg-yellow-600 hover:bg-yellow-700"
            data-testid="setup-mega-sponsor-btn"
          >
            5. Setup Mega Sponsor
          </Button>
        </div>
        {seedResults && (
          <div className="mt-4 text-sm">
            <p className="text-orange-800 font-medium mb-2">Site Pages Status:</p>
            <div className="flex flex-wrap gap-2">
              {seedResults.map((r, idx) => (
                <span key={idx} className={`px-2 py-1 rounded text-xs ${
                  r.status === "inserted" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                }`}>
                  {r.slug}: {r.status}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Cloudinary Media Panel - Zero Effort Fix */}
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-300 rounded-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
            <Cloud className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-heading text-lg text-blue-800">Cloudinary Media Manager</h3>
            <p className="text-blue-600 text-sm">Permanent image storage - never lose images again</p>
          </div>
        </div>
        
        {/* Health Status */}
        {cloudinaryHealth && (
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-white rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-gray-800">{cloudinaryHealth.total}</p>
              <p className="text-xs text-gray-500">Total Images</p>
            </div>
            <div className="bg-white rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-green-600">{cloudinaryHealth.cloudinary}</p>
              <p className="text-xs text-gray-500">On Cloudinary ✓</p>
            </div>
            <div className="bg-white rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-amber-600">{cloudinaryHealth.unstable}</p>
              <p className="text-xs text-gray-500">Need Migration</p>
            </div>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={handleCloudinaryHealthCheck}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="cloudinary-health-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Check Media Health
          </Button>
          <Button
            onClick={handleCloudinaryMigrateAll}
            disabled={cloudinaryMigrating}
            className="bg-green-600 hover:bg-green-700"
            data-testid="cloudinary-migrate-btn"
          >
            {cloudinaryMigrating ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Migrating...
              </>
            ) : (
              <>
                <Cloud className="w-4 h-4 mr-2" />
                Fix All Media (Cloudinary)
              </>
            )}
          </Button>
        </div>
        
        {/* Migration Results */}
        {cloudinaryMigrateResults && (
          <div className="mt-4 bg-white rounded-lg p-4">
            <h4 className="font-medium text-gray-800 mb-2">Migration Results</h4>
            <div className="grid grid-cols-4 gap-2 text-sm mb-3">
              <div className="text-center p-2 bg-gray-100 rounded">
                <p className="font-bold">{cloudinaryMigrateResults.scanned}</p>
                <p className="text-xs text-gray-500">Scanned</p>
              </div>
              <div className="text-center p-2 bg-green-100 rounded">
                <p className="font-bold text-green-700">{cloudinaryMigrateResults.migrated}</p>
                <p className="text-xs text-gray-500">Migrated</p>
              </div>
              <div className="text-center p-2 bg-gray-100 rounded">
                <p className="font-bold">{cloudinaryMigrateResults.skipped}</p>
                <p className="text-xs text-gray-500">Skipped</p>
              </div>
              <div className="text-center p-2 bg-red-100 rounded">
                <p className="font-bold text-red-700">{cloudinaryMigrateResults.failed?.length || 0}</p>
                <p className="text-xs text-gray-500">Failed</p>
              </div>
            </div>
            {cloudinaryMigrateResults.failed?.length > 0 && (
              <div className="text-xs text-red-600">
                <p className="font-medium mb-1">Failed items:</p>
                {cloudinaryMigrateResults.failed.slice(0, 5).map((f, i) => (
                  <p key={i} className="truncate">• {f.title || f.id}: {f.error}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Integrations Status Panel */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-300 rounded-sm p-6" data-testid="integrations-panel">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-heading text-lg text-indigo-800">Integration Settings</h3>
              <p className="text-indigo-600 text-sm">Stripe Payments & Resend Emails</p>
            </div>
          </div>
          <Button
            onClick={fetchIntegrationsStatus}
            variant="outline"
            size="sm"
            className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
        
        {integrationsStatus ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Stripe Status */}
            <div className="bg-white rounded-lg p-4 border border-indigo-100" data-testid="stripe-integration-card">
              <div className="flex items-center gap-2 mb-3">
                <CreditCard className="w-5 h-5 text-indigo-600" />
                <h4 className="font-semibold text-gray-800">Stripe Payments</h4>
                {integrationsStatus.stripe?.mode === "test" ? (
                  <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">Test Mode</span>
                ) : integrationsStatus.stripe?.mode === "live" ? (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Live</span>
                ) : (
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">Not Configured</span>
                )}
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  {integrationsStatus.stripe?.webhook_configured ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-yellow-500" />
                  )}
                  <span className="text-gray-600">
                    Webhook: {integrationsStatus.stripe?.webhook_configured ? "Configured" : "Not configured"}
                  </span>
                </div>
                
                {integrationsStatus.stripe?.last_webhook && (
                  <p className="text-xs text-gray-500">
                    Last webhook: {new Date(integrationsStatus.stripe.last_webhook).toLocaleString()}
                  </p>
                )}
                
                {!integrationsStatus.stripe?.webhook_configured && (
                  <div className="mt-2 p-2 bg-yellow-50 rounded text-xs text-yellow-700">
                    <p className="font-medium mb-1">Setup Stripe Webhook:</p>
                    <ol className="list-decimal ml-4 space-y-1">
                      <li>Go to Stripe Dashboard → Developers → Webhooks</li>
                      <li>Add endpoint: <code className="bg-yellow-100 px-1 rounded">{window.location.origin}/api/webhook/stripe</code></li>
                      <li>Select event: <code className="bg-yellow-100 px-1 rounded">checkout.session.completed</code></li>
                      <li>Copy the signing secret to your environment variables</li>
                    </ol>
                  </div>
                )}
              </div>
            </div>
            
            {/* Resend Status */}
            <div className="bg-white rounded-lg p-4 border border-indigo-100" data-testid="resend-integration-card">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <h4 className="font-semibold text-gray-800">Resend Emails</h4>
                {integrationsStatus.resend?.mode === "production" ? (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Production</span>
                ) : integrationsStatus.resend?.mode === "sandbox" ? (
                  <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">Sandbox</span>
                ) : (
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">Not Configured</span>
                )}
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  {integrationsStatus.resend?.domain_verified ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-yellow-500" />
                  )}
                  <span className="text-gray-600">
                    Domain: {integrationsStatus.resend?.domain_verified ? "Verified" : "Not verified"}
                  </span>
                </div>
                
                <p className="text-xs text-gray-500">
                  Sender: {integrationsStatus.resend?.sender_email || "Not set"}
                </p>
                <p className="text-xs text-gray-500">
                  Emails sent: {integrationsStatus.resend?.emails_sent || 0}
                </p>
                
                {integrationsStatus.resend?.mode === "sandbox" && (
                  <div className="mt-2 p-2 bg-yellow-50 rounded text-xs text-yellow-700">
                    <p className="font-medium mb-1">Enable Production Emails:</p>
                    <ol className="list-decimal ml-4 space-y-1">
                      <li>Go to <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline">resend.com/domains</a></li>
                      <li>Add your domain (e.g., tatvamasi.ca)</li>
                      <li>Add the DNS records provided by Resend to your domain</li>
                      <li>Wait for verification (24-48 hours)</li>
                      <li>Update SENDER_EMAIL to your verified domain (e.g., noreply@tatvamasi.ca)</li>
                    </ol>
                    <p className="mt-2 text-yellow-800">
                      <strong>Note:</strong> In sandbox mode, emails are only sent to admin email.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500">
            Loading integrations status...
          </div>
        )}
      </div>

      {/* Initialize Site Pages */}
      <div className="bg-white p-6 rounded-sm border border-kerala-gold/10">
        <h2 className="font-heading text-xl text-kerala-green mb-2">Initialize Site Pages</h2>
        <p className="text-gray-600 text-sm mb-4">
          Seed default content for public pages (Home, About, Contact, etc.). Only inserts missing pages.
        </p>
        <Button
          onClick={handleSeedSitePages}
          disabled={seedingPages}
          className="bg-kerala-green hover:bg-kerala-green/90"
          data-testid="seed-site-pages-btn"
        >
          {seedingPages ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              Initializing...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Database className="w-4 h-4" />
              Initialize Site Pages
            </span>
          )}
        </Button>

        {seedResults && (
          <div className="mt-4 border border-kerala-gold/20 rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-kerala-cream">
                <tr>
                  <th className="text-left px-3 py-2 text-kerala-green">Page Slug</th>
                  <th className="text-left px-3 py-2 text-kerala-green">Status</th>
                </tr>
              </thead>
              <tbody>
                {seedResults.map((r, idx) => (
                  <tr key={idx} className="border-t border-kerala-gold/10">
                    <td className="px-3 py-2">{r.slug}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        r.status === "inserted" 
                          ? "bg-green-100 text-green-700" 
                          : "bg-gray-100 text-gray-600"
                      }`}>
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
